import { Box, Button, Typography, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ModalContentWrapper } from "components/primitives/ModalContentWrapper";
import useEmblaCarousel from "embla-carousel-react";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ModalContentProps } from "types/mainTypes";
import { TutorialStepper } from "./TutorialStepper";
import { getTutorialSteps } from "./TutorialSteps";

/**
 * Tutorial modal content
 * @param onClose
 * @constructor
 */
export const TutorialModalContent = ({ onClose }: ModalContentProps) => {
	const theme = useTheme();
	const [activeStep, setActiveStep] = useState(0);
	const { t } = useTranslation();
	const { trackTutorialStep } = useMixpanelTrackers();
	const tutorialSteps = getTutorialSteps();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		direction: theme.direction === "rtl" ? "rtl" : "ltr",
	});

	useEffect(() => {
		trackTutorialStep(activeStep);
	}, [activeStep, trackTutorialStep]);

	useEffect(() => {
		if (!emblaApi) {
			return;
		}
		const onSelect = () => {
			setActiveStep(emblaApi.selectedScrollSnap());
		};
		emblaApi.on("select", onSelect);
		return () => {
			emblaApi.off("select", onSelect);
		};
	}, [emblaApi]);

	const handleStepChange = (step: number) => {
		emblaApi?.scrollTo(step);
		setActiveStep(step);
	};

	return (
		<ModalContentWrapper>
			<Typography variant="promoHeader" alignSelf="flex-start" mb={3}>
				{t("tutorial.title")}
			</Typography>
			<Box
				ref={emblaRef}
				sx={{
					overflow: "hidden",
					width: "calc(100vw - 80px)",
					mb: 4,
				}}
			>
				<Box sx={{ display: "flex" }}>
					{tutorialSteps.map((step) => {
						const SvgComponent = step.svg;
						return (
							<Box key={step.title} sx={{ flex: "0 0 100%", minWidth: 0 }}>
								<StyledStepIllustrationWrapper>
									<SvgComponent />
								</StyledStepIllustrationWrapper>
							</Box>
						);
					})}
				</Box>
			</Box>
			<TutorialStepper activeStep={activeStep} onStepChange={handleStepChange} />
			<Typography variant="cta" textAlign="center" width="90%" mb={1}>
				{t(tutorialSteps[activeStep].title)}
			</Typography>

			<StyledCloseButton color="black" variant="contained" onClick={() => onClose?.()}>
				{t("tutorial.confirmButton")}
			</StyledCloseButton>
			<Typography variant="badge" textAlign="center" width="400px">
				version: {import.meta.env.APP_VERSION} ({import.meta.env.VITE_BUILD_NUMBER ?? "12345"})
			</Typography>
		</ModalContentWrapper>
	);
};

const StyledStepIllustrationWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	width: 640,
	overflow: "hidden",
	padding: `8px ${theme.spacing(3)}`,
	margin: "auto",
}));

const StyledCloseButton = styled(Button)(({ theme }) => ({
	color: theme.palette.common.white,
	padding: "16px 24px",
	marginTop: "auto",
	marginBottom: "16px",
}));
